import { Component, OnInit, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { SummaryCardComponent } from '@next/core-lib/components';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  AbstractFormDialogData,
  FormDialogComponent,
} from '@next/core-lib/dialog';
import { AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { skipWhile, take } from 'rxjs/operators';

import { AttentionLimits, AttentionLimitsState } from '../../models/attention-limits.model';
import { getAttentionLimits } from '../../store/selectors/attention-limits.selectors';

export interface ProductionSettingsDialogResultData {
  attentionLimits: AttentionLimits;
}

@Component({
  selector: 'app-production-settings',
  templateUrl: './production-settings.component.html',
  styleUrls: ['./production-settings.component.scss'],
  imports: [
    FormDialogComponent,
    FormsModule,
    ReactiveFormsModule,
    SummaryCardComponent,
    MatFormField,
    MatLabel,
    MatInput,
    AgrNumberDirective,
    MatSlideToggle,
    TranslatePipe,
  ],
})
export class ProductionSettingsComponent
  extends AbstractFormDialog<ProductionSettingsDialogResultData>
  implements OnInit
{
  data = inject<AbstractFormDialogData>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<
      MatDialogRef<
        ProductionSettingsComponent,
        AbstractDialogResultData<ProductionSettingsDialogResultData>
      >
    >(MatDialogRef);
  private readonly store = inject<Store<AttentionLimitsState>>(Store);
  private readonly localeService = inject(LocaleService);

  public attentionLimits: AttentionLimits;
  public formGroup = new FormGroup({
    fromLactationDays: new FormControl(null, [
      Validators.required,
      Validators.min(0),
      Validators.max(999),
    ]),
    toLactationDays: new FormControl(null, [
      Validators.required,
      Validators.min(0),
      Validators.max(999),
    ]),
    minimumFatPercentage: new FormControl(null, [
      Validators.required,
      Validators.min(1),
      Validators.max(5),
    ]),
    minimumProteinPercentage: new FormControl(null, [
      Validators.required,
      Validators.min(1),
      Validators.max(5),
    ]),
    maximumFatProteinPercentage: new FormControl(null, [
      Validators.required,
      Validators.min(0),
      Validators.max(99),
    ]),
    whenMinimumProteinPercentage: new FormControl(null, [
      Validators.required,
      Validators.min(0),
      Validators.max(99),
    ]),
    decreasePercentageKgMilk: new FormControl(null, [
      Validators.required,
      Validators.min(0),
      Validators.max(99),
    ]),
    fatPercentageLowerThenProteinPercentage: new FormControl(),
    maxSomaticCellCountCow: new FormControl(),
    maxSomaticCellCountHeifer: new FormControl(),
  });

  private attentionLimits$ = this.store.select(getAttentionLimits);

  constructor() {
    super();
    const data = this.data;

    data.title = this.localeService.translate(
      'dairyresults.individual-production.settings.form-title',
    );
  }

  ngOnInit(): void {
    this.attentionLimits$
      .pipe(
        skipWhile((attentionLimits) => !attentionLimits),
        take(1),
      )
      .subscribe((attentionLimits) => {
        this.attentionLimits = attentionLimits;

        this.formGroup.setValue({
          fromLactationDays: attentionLimits.production.fromLactationDays,
          toLactationDays: attentionLimits.production.toLactationDays,
          minimumFatPercentage: attentionLimits.production.minimumFatPercentage,
          minimumProteinPercentage: attentionLimits.production.minimumProteinPercentage,
          maximumFatProteinPercentage: attentionLimits.production.maximumFatProteinPercentage,
          whenMinimumProteinPercentage: attentionLimits.production.whenMinimumProteinPercentage,
          decreasePercentageKgMilk: attentionLimits.production.decreasePercentageKgMilk,
          fatPercentageLowerThenProteinPercentage:
            attentionLimits.production.fatPercentageLowerThenProteinPercentage,
          maxSomaticCellCountCow: attentionLimits.production.maxSomaticCellCountCow,
          maxSomaticCellCountHeifer: attentionLimits.production.maxSomaticCellCountHeifer,
        });
        this.formGroup.controls.fromLactationDays.updateValueAndValidity({ emitEvent: false });
      });
  }

  public update() {
    this.confirm({
      attentionLimits: {
        production: {
          fromLactationDays: this.formGroup.get('fromLactationDays').value,
          toLactationDays: this.formGroup.get('toLactationDays').value,
          minimumFatPercentage: this.formGroup.get('minimumFatPercentage').value,
          minimumProteinPercentage: this.formGroup.get('minimumProteinPercentage').value,
          maximumFatProteinPercentage: this.formGroup.get('maximumFatProteinPercentage').value,
          whenMinimumProteinPercentage: this.formGroup.get('whenMinimumProteinPercentage').value,
          decreasePercentageKgMilk: this.formGroup.get('decreasePercentageKgMilk').value,
          fatPercentageLowerThenProteinPercentage: this.formGroup.get(
            'fatPercentageLowerThenProteinPercentage',
          ).value,
          maxSomaticCellCountCow: this.formGroup.get('maxSomaticCellCountCow').value,
          maxSomaticCellCountHeifer: this.formGroup.get('maxSomaticCellCountHeifer').value,
        },
      },
    });
  }
}
